import { useState } from 'react'
import styled, { css } from 'styled-components'
import { useShowDialog } from 'App'
import type { VehicleServiceStatus } from 'models/Vehicles/VehicleStateModel'
import { theme } from 'styles/theme'
import { PrimaryButton, SecondaryButton } from '../Button/Button'
import { Column, Row } from '../Table/Table'
import { SERVICE_STATUSES, serviceStatusLooks } from './VehicleServiceStatusBadge'

const Title = styled.h2`
    font-size: 20px;
    font-weight: ${theme.fontWeights.semiBold};
`

const SubTitle = styled.span`
    color: ${theme.colors.SecondaryGrey};
    font-size: ${theme.fontSizes.smaller};
`

// The native radio input is kept (hidden) for the selection and keyboard behavior
const HiddenRadioInput = styled.input.attrs({ type: 'radio' })`
    position: absolute;
    opacity: 0;
    pointer-events: none;
`

const StatusOption = styled.label<{ $color: string, $borderColor: string, $backgroundColor: string }>`
    display: flex;
    align-items: center;
    gap: 8px;
    block-size: 42px;
    padding: 0 12px;
    border: 1px solid #CDCDCD;
    border-radius: 8px;
    color: ${theme.colors.SecondaryGrey};
    font-weight: ${theme.fontWeights.semiBold};
    cursor: pointer;
    :has(:checked) {
        ${({ $color, $borderColor, $backgroundColor }) => css`
            border-color: ${$borderColor};
            background-color: ${$backgroundColor};
            color: ${$color};
        `}
    }
    :has(:focus-visible) {
        outline: 1px solid ${theme.colors.MainYellow};
        outline-offset: 2px;
    }
`

const StatusDot = styled.span<{ $color: string }>`
    flex: none;
    inline-size: 6px;
    block-size: 6px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
`

// The vehicle's number when there is one vehicle, as in the profile update popup
const getSubTitle = (vehicleIds: ReadonlyArray<number>) => {
    const [singleVehicleId] = vehicleIds
    return vehicleIds.length === 1 && singleVehicleId !== undefined
        ? `Vehicle ${singleVehicleId}`
        : `${vehicleIds.length} Vehicles selected`
}

interface ChangeStatusContentProps {
    vehicleIds: ReadonlyArray<number>
    // Selected when the popup opens
    initialStatus: VehicleServiceStatus | null
    onApply: (serviceStatus: VehicleServiceStatus) => void
}

const ChangeStatusContent = ({ vehicleIds, initialStatus, onApply }: ChangeStatusContentProps) => {
    const [selectedStatus, setSelectedStatus] = useState<VehicleServiceStatus | null>(initialStatus)
    const showDialog = useShowDialog()
    const hideDialog = () => { showDialog(null) }

    return (
        <Column style={{ alignItems: 'center', gap: '1.5em', width: '100%' }}>
            <Column style={{ alignItems: 'center', gap: '0.5em' }}>
                <Title>Change status</Title>
                <SubTitle>{ getSubTitle(vehicleIds) }</SubTitle>
            </Column>
            <Column style={{ gap: '13px', width: '100%' }}>
                { SERVICE_STATUSES.map(serviceStatus => {
                    const { title, color, borderColor, backgroundColor } = serviceStatusLooks[serviceStatus]
                    return (
                        <StatusOption {...{ key: serviceStatus, $color: color, $borderColor: borderColor, $backgroundColor: backgroundColor }}>
                            <HiddenRadioInput {...{
                                name: 'vehicle-service-status',
                                checked: selectedStatus === serviceStatus,
                                onChange: () => { setSelectedStatus(serviceStatus) },
                            }} />
                            <StatusDot {...{ $color: color }} />
                            { title }
                        </StatusOption>
                    )
                }) }
            </Column>
            <Row style={{ gap: '20px' }}>
                <SecondaryButton {...{
                    text: 'Cancel',
                    style: { minInlineSize: '150px' },
                    onClick: hideDialog,
                }} />
                <PrimaryButton {...{
                    text: 'Apply',
                    style: { minInlineSize: '150px' },
                    // Also disabled while the vehicles' current status is selected, as nothing would change
                    isDisabled: selectedStatus === null || selectedStatus === initialStatus,
                    onClick: () => {
                        if (selectedStatus !== null) {
                            hideDialog()
                            onApply(selectedStatus)
                        }
                    },
                }} />
            </Row>
        </Column>
    )
}

interface ShowChangeStatusDialogParams {
    showDialog: ReturnType<typeof useShowDialog>
    vehicleIds: ReadonlyArray<number>
    // The current status of each of the vehicles, undefined when not known
    currentServiceStatuses: ReadonlyArray<VehicleServiceStatus | undefined>
    setVehiclesServiceStatus: (vehicleIds: ReadonlyArray<number>, serviceStatus: VehicleServiceStatus) => Promise<boolean>
    // Called once the vehicles were set, also when some of them failed
    onApplied?: (isAllSet: boolean) => void
}

// Opens the popup for changing the service status of one vehicle or more
// The popup opens with the vehicles' current status selected, when they all have the same known status.
export function showChangeStatusDialog({ showDialog, vehicleIds, currentServiceStatuses, setVehiclesServiceStatus, onApplied }: ShowChangeStatusDialogParams) {
    const [firstStatus] = currentServiceStatuses
    const sharedStatus = firstStatus !== undefined && currentServiceStatuses.every(status => status === firstStatus) ? firstStatus : null
    showDialog({
        content: (
            <ChangeStatusContent {...{
                vehicleIds,
                initialStatus: sharedStatus,
                onApply: (serviceStatus) => {
                    void setVehiclesServiceStatus(vehicleIds, serviceStatus).then(isAllSet => onApplied?.(isAllSet))
                },
            }} />
        ),
    })
}
